import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import AuthModal from '@/components/auth/AuthModal';
import PromptComposer from '@/components/writer/guest/PromptComposer';
import ProductStage from '@/components/product/ProductStage';
import { useWriterPromptStart } from '@/hooks/useWriterPromptStart';

interface HomeWriterEntryProps {
  onActiveChange: (active: boolean) => void;
}

const HomeWriterEntry: React.FC<HomeWriterEntryProps> = ({ onActiveChange }) => {
  const prefersReducedMotion = useReducedMotion();
  const { authOpen, setAuthOpen, busy, submitPrompt, continueAfterAuth, preserveOAuthDestination } = useWriterPromptStart();

  return (
    <>
      <section className="relative min-h-[min(700px,92svh)] overflow-hidden px-5 pb-12 pt-24 md:px-8 md:pb-16 md:pt-28" aria-labelledby="home-title">
        <ProductStage className="absolute inset-0" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center">
          <motion.h1
            id="home-title"
            className="max-w-[16ch] text-4xl font-semibold leading-[1.08] tracking-normal sm:text-5xl md:text-6xl"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Write the paper. Know what reviewers will challenge.
          </motion.h1>
          <p className="mt-5 max-w-[54ch] text-base leading-7 text-muted-foreground md:text-lg">
            Write in LaTeX with real citations and a compiled PDF, then review your manuscript before submission.
          </p>

          <div className="mt-8 w-full text-left">
            <PromptComposer
              onSubmitPrompt={(prompt) => void submitPrompt(prompt)}
              onActiveChange={onActiveChange}
              showChips={false}
              autoFocus={false}
              idleHint={null}
              busy={busy}
            />
          </div>

          <Link
            to="/upload"
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-primary"
            data-track="hero_review_my_paper"
            data-track-category="conversion"
          >
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            Already have a draft? Review my paper
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>Free to start</span>
            <span aria-hidden="true">·</span>
            <Link to="/privacy-policy" className="hover:text-foreground">Private until you publish</Link>
            <span aria-hidden="true">·</span>
            <Link to="/eu-processing" className="hover:text-foreground">Processed in Europe</Link>
          </div>
        </div>
      </section>

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onAuthSuccess={continueAfterAuth}
        message="Create a free account to start writing. Your prompt is saved and will run right after sign-in."
        onBeforeGoogleAuth={preserveOAuthDestination}
      />
    </>
  );
};

export default HomeWriterEntry;